import React, { useState, useEffect } from "react";
import {
    Box,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Slider,
    TextField,
    Typography,
    Paper,
    IconButton,
    Stack,
} from "@mui/material";
import { styled } from "@mui/system";
import ArrowDownIcon from "../../../svg/ArrowDownIcon";
import RemoveIcon from "../../../svg/RemoveIcon";
import { StyledMenu } from "./Styled";
import { BoxWrapper } from "../../DropdownMenu.tsx/Styled";
import { color } from "../../../styles";

interface DropDownProps {
    menuText: string;
    handleSetAnchor: any;
    style?: any;
}

const DropDown = ({ menuText, handleSetAnchor, style }: DropDownProps) => {
    return (
        <BoxWrapper onClick={handleSetAnchor} style={style}>
            {menuText}
            <ArrowDownIcon color="black" />
        </BoxWrapper>
    );
};

interface PriceRangeFilterProps {
    menuText: string;
    changeStackFilteredOptions: Function;
    filterType: any;
}

const PriceRangeFilter = ({
    menuText,
    changeStackFilteredOptions,
    filterType,
}: PriceRangeFilterProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [anchorWidth, setAnchorWidth] = useState(0);
    const open = Boolean(anchorEl);
    const [priceRange, setPriceRange] = useState([10000000, 10000000000]);
    const [radioValue, setRadioValue] = useState("all");
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");

    useEffect(() => {
        if (anchorEl) {
            setAnchorWidth(anchorEl.offsetWidth);
        }
    }, [anchorEl]);

    const handlePriceChange = (min: string, max: string) => {
        const processedValue = [];

        if (min) {
            processedValue.push({
                query: min,
                fields: ["price__gte"],
            });
        }

        if (max) {
            processedValue.push({
                query: max,
                fields: ["price__lte"],
            });
        }

        changeStackFilteredOptions(processedValue, filterType);
    };

    const handleSliderChange = (event: any, newValue: any) => {
        setPriceRange(newValue);
        setMinPrice(newValue[0].toString());
        setMaxPrice(newValue[1].toString());
        setRadioValue("custom");
        handlePriceChange(newValue[0].toString(), newValue[1].toString());
    };

    const handleRadioChange = (event: any) => {
        const value = event.target.value;
        setRadioValue(value);

        switch (value) {
            case "below100m":
                setPriceRange([0, 100000000]);
                setMinPrice("0");
                setMaxPrice("100000000");
                handlePriceChange("0", "100000000");
                break;
            case "100mto1b":
                setPriceRange([100000000, 1000000000]);
                setMinPrice("100000000");
                setMaxPrice("1000000000");
                handlePriceChange("100000000", "1000000000");
                break;
            case "1bto5b":
                setPriceRange([1000000000, 5000000000]);
                setMinPrice("1000000000");
                setMaxPrice("5000000000");
                handlePriceChange("1000000000", "5000000000");
                break;
            case "above5b":
                setPriceRange([5000000000, 10000000000]);
                setMinPrice("5000000000");
                setMaxPrice("10000000000");
                handlePriceChange("5000000000", "10000000000");
                break;
            default:
                setPriceRange([10000000, 10000000000]);
                setMinPrice("10000000");
                setMaxPrice("10000000000");
                handlePriceChange("10000000", "10000000000");
        }
    };

    const handleMinPriceChange = (event: any) => {
        const value = event.target.value;
        if (value === "" || /^\d+$/.test(value)) {
            setMinPrice(value);
            if (value !== "") {
                setPriceRange([Number(value), priceRange[1]]);
                setRadioValue("custom");
                handlePriceChange(value, maxPrice);
            }
        }
    };

    const handleMaxPriceChange = (event: any) => {
        const value = event.target.value;
        if (value === "" || /^\d+$/.test(value)) {
            setMaxPrice(value);
            if (value !== "") {
                setPriceRange([priceRange[0], Number(value)]);
                setRadioValue("custom");
                handlePriceChange(minPrice, value);
            }
        }
    };

    const handleReset = () => {
        setPriceRange([10000000, 10000000000]);
        setRadioValue("all");
        setMinPrice("");
        setMaxPrice("");
        handlePriceChange("10000000", "10000000000");
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const formatPrice = (value: number) => {
        if (value >= 1000000000) {
            return `${value / 1000000000} tỉ`;
        }
        return `${value / 1000000} triệu`;
    };

    return (
        <Box
            data-testid="price-range-filter"
            sx={{ width: "100%", maxWidth: 400, margin: "0 auto" }}
        >
            <DropDown
                menuText={menuText}
                handleSetAnchor={handleClick}
                style={{ height: "30px", fontSize: "12px", color: color.secondary.DARK_GREY }}
            />
            <StyledMenu
                id="price-range-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    "aria-labelledby": "price-range-button",
                }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                slotProps={{
                    paper: {
                        style: {
                            width: "350px",
                            maxHeight: "400px",
                            overflowY: "auto",
                            marginTop: "4px",
                        },
                    },
                }}
            >
                <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            label="Thấp nhất"
                            value={minPrice}
                            onChange={handleMinPriceChange}
                            type="text"
                            size="small"
                            placeholder="Từ"
                            InputProps={{
                                style: { fontSize: "16px", fontWeight: "400" },
                            }}
                            InputLabelProps={{
                                style: {
                                    color: color.secondary.DARK_GREY,
                                    fontSize: "16px",
                                    fontWeight: "300",
                                },
                            }}
                            aria-label="Minimum price input"
                        />
                        <Typography>-</Typography>
                        <TextField
                            label="Cao nhất"
                            value={maxPrice}
                            onChange={handleMaxPriceChange}
                            type="text"
                            size="small"
                            placeholder="Đến"
                            InputProps={{
                                style: { fontSize: "16px", fontWeight: "400" },
                            }}
                            InputLabelProps={{
                                style: {
                                    color: color.secondary.DARK_GREY,
                                    fontSize: "16px",
                                    fontWeight: "300",
                                },
                            }}
                            aria-label="Maximum price input"
                        />
                        <IconButton
                            onClick={handleReset}
                            aria-label="Reset price filter"
                            size="small"
                        >
                            <RemoveIcon />
                        </IconButton>
                    </Stack>

                    <Box
                        sx={{
                            width: "calc(100% - 48px)",
                            margin: "0 auto",
                            paddingLeft: "8px",
                            paddingRight: "8px",
                        }}
                    >
                        <Slider
                            value={priceRange}
                            onChange={handleSliderChange}
                            valueLabelDisplay="auto"
                            valueLabelFormat={formatPrice}
                            min={10000000}
                            max={10000000000}
                            step={10000000}
                            sx={{
                                "& .MuiSlider-thumb": {
                                    backgroundColor: "white",
                                    border: "2px solid currentColor",
                                    "&:hover, &.Mui-active": {
                                        boxShadow:
                                            "0 0 0 8px rgba(25, 118, 210, 0.16)",
                                    },
                                },
                            }}
                        />
                    </Box>

                    <FormControl component="fieldset">
                        <RadioGroup
                            value={radioValue}
                            onChange={handleRadioChange}
                        >
                            <FormControlLabel
                                value="all"
                                control={<Radio />}
                                label="Tất cả mức giá"
                            />
                            <FormControlLabel
                                value="below100m"
                                control={<Radio />}
                                label="Dưới 100 triệu đồng"
                            />
                            <FormControlLabel
                                value="100mto1b"
                                control={<Radio />}
                                label="Từ 100 triệu đến 1 tỉ đồng"
                            />
                            <FormControlLabel
                                value="1bto5b"
                                control={<Radio />}
                                label="Từ 1 tỉ đến 5 tỉ đồng"
                            />
                            <FormControlLabel
                                value="above5b"
                                control={<Radio />}
                                label="Trên 5 tỉ đồng"
                            />
                        </RadioGroup>
                    </FormControl>
                </Stack>
            </StyledMenu>
        </Box>
    );
};

export default PriceRangeFilter;
